/**
 * Deep-merge a patient PATCH body into a cached patient snapshot.
 *
 * PATCH collection fields contain operations, while the patient cache must
 * contain resolved records. Applying the same optimistic body repeatedly
 * (hydrate, refetch, retry) must therefore be idempotent: updates replace one
 * matching row, deletes disappear, and creates never multiply.
 */

type AnyRecord = Record<string, unknown>;

type CollectionDescriptor = {
  path: string[];
  identityKeys: string[];
};

const COLLECTIONS: CollectionDescriptor[] = [
  {
    // Plan Builder edits the patient's primary address as a one-row list.
    // Position is the only stable pre-ID identity because postalCode itself
    // is editable.
    path: ["addresses"],
    identityKeys: [],
  },
  {
    path: ["testResults"],
    identityKeys: [
      "type",
      "urineTestCategory",
      "bloodTestCategory",
      "imagingTestCategory",
      "samplingTestCategory",
      "geneticsTestCategory",
      "semenFluidTestCategory",
      "clinicalTestCategory",
    ],
  },
  {
    path: ["fertilityHistory", "infertilityHistory", "causes"],
    identityKeys: ["cause", "other"],
  },
  {
    path: ["fertilityHistory", "infertilityHistory", "diagnoses"],
    identityKeys: ["diagnosis", "other"],
  },
  {
    path: ["obGynHistory", "pregnancies"],
    // Rows may be captured before the backend assigns an ID, and every field
    // can be edited. Position is the only stable identity during that window.
    identityKeys: [],
  },
  {
    path: ["obGynHistory", "contraceptions"],
    identityKeys: ["method", "other"],
  },
  {
    path: ["obGynHistory", "menstrualHealth", "symptoms"],
    identityKeys: ["symptom", "other"],
  },
  {
    path: ["obGynHistory", "sexualHealth", "papSmearProcedures"],
    identityKeys: ["procedure", "other"],
  },
  {
    path: ["obGynHistory", "sexualHealth", "stds"],
    identityKeys: ["stdType", "other"],
  },
  {
    path: ["obGynHistory", "sexualHealth", "vaginalInfectionHistories"],
    identityKeys: ["vaginalInfectionHistory"],
  },
  {
    path: ["maleHistory", "contraceptions"],
    identityKeys: ["method", "other"],
  },
  {
    path: ["medicalHistory", "allergies"],
    identityKeys: ["allergy", "name", "other"],
  },
  {
    path: ["medicalHistory", "diseases"],
    identityKeys: ["disease", "other"],
  },
  {
    path: ["medicalHistory", "surgeries"],
    identityKeys: ["surgery", "name", "other"],
  },
  {
    path: ["medicalHistory", "breastConditions"],
    identityKeys: ["condition", "other"],
  },
  {
    path: ["medicalHistory", "cancers"],
    identityKeys: ["cancer", "type", "cancerType", "other"],
  },
  {
    path: ["medicalHistory", "cancers", "*", "treatments"],
    identityKeys: ["treatmentType", "treatment", "type", "other"],
  },
  {
    path: ["medicalHistory", "medications"],
    identityKeys: ["name", "brandName", "medication"],
  },
  {
    path: ["medicalHistory", "vaccinations"],
    identityKeys: ["type", "name", "vaccine"],
  },
  {
    path: ["lifestyle", "addictions"],
    identityKeys: ["substance", "other"],
  },
  {
    path: ["lifestyle", "exposures"],
    identityKeys: ["exposure", "type", "other"],
  },
  { path: ["lifestyle", "diets"], identityKeys: ["diet", "type", "other"] },
  {
    path: ["lifestyle", "dietSupplements"],
    identityKeys: ["supplement", "name", "other"],
  },
  {
    path: ["familyHistory", "ancestryHistory"],
    identityKeys: ["ancestry", "ethnicity", "other"],
  },
  {
    path: ["familyHistory", "familyMemberWithFertilityIssues"],
    identityKeys: ["familyMember", "relationship", "other"],
  },
];

function isRecord(value: unknown): value is AnyRecord {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function mergeDefined(current: AnyRecord, update: AnyRecord): AnyRecord {
  const merged: AnyRecord = { ...current };
  for (const [key, value] of Object.entries(update)) {
    if (value !== undefined) merged[key] = value;
  }
  return merged;
}

function operationFields(operation: AnyRecord): AnyRecord {
  const fields = { ...operation };
  delete fields.op;
  return fields;
}

function comparableFingerprint(record: AnyRecord): string {
  return JSON.stringify(
    Object.fromEntries(
      Object.entries(record)
        .filter(
          ([key, value]) => key !== "id" && key !== "op" && value !== undefined,
        )
        .sort(([first], [second]) => first.localeCompare(second)),
    ),
  );
}

function logicalKey(
  record: AnyRecord,
  identityKeys: string[],
): string | undefined {
  const values = identityKeys
    .filter((key) => record[key] !== undefined && record[key] !== null)
    .map((key) => [key, record[key]]);
  return values.length > 0 ? JSON.stringify(values) : undefined;
}

/**
 * Row-identity keys for a top-level patient collection write scope (dot
 * path, e.g. "lifestyle.addictions"), as used by reconcileCollection.
 * Returns undefined for scopes that are not a known patient collection so
 * callers can fail closed.
 */
export function collectionIdentityKeysForScope(
  scope: string,
): string[] | undefined {
  return COLLECTIONS.find((entry) => entry.path.join(".") === scope)
    ?.identityKeys;
}

/**
 * Stable identities of one collection row, in the same precedence
 * reconcileCollection uses to match operations to records: the server id
 * first, then the logical identity key. Empty when the row has neither —
 * such rows cannot be proven addressed by another write.
 */
export function collectionRowIdentities(
  row: Record<string, unknown>,
  identityKeys: string[],
): string[] {
  const identities: string[] = [];
  if (typeof row.id === "string") identities.push(`id:${row.id}`);
  const key = logicalKey(row, identityKeys);
  if (key !== undefined) identities.push(`key:${key}`);
  return identities;
}

function matchingIndex(
  collection: AnyRecord[],
  operation: AnyRecord,
  identityKeys: string[],
  consumed: Set<number>,
): number {
  if (typeof operation.id === "string") {
    return collection.findIndex((record) => record.id === operation.id);
  }

  const key = logicalKey(operation, identityKeys);
  const fingerprint = comparableFingerprint(operation);
  return collection.findIndex((record, index) => {
    if (consumed.has(index)) return false;
    if (identityKeys.length === 0) return true;
    const recordKey = logicalKey(record, identityKeys);
    return key !== undefined
      ? recordKey === key
      : fingerprint === "{}" || comparableFingerprint(record) === fingerprint;
  });
}

function reconcileCollection(
  current: unknown,
  incoming: unknown,
  identityKeys: string[],
): AnyRecord[] {
  const collection = Array.isArray(current)
    ? current.filter(isRecord).map((record) => ({ ...record }))
    : [];
  if (!Array.isArray(incoming)) return collection;

  const consumed = new Set<number>();
  for (const value of incoming) {
    if (!isRecord(value)) continue;
    const index = matchingIndex(collection, value, identityKeys, consumed);

    if (value.op === "delete") {
      if (index >= 0) {
        collection.splice(index, 1);
        const shifted = new Set<number>();
        for (const consumedIndex of consumed) {
          if (consumedIndex < index) shifted.add(consumedIndex);
          if (consumedIndex > index) shifted.add(consumedIndex - 1);
        }
        consumed.clear();
        for (const consumedIndex of shifted) consumed.add(consumedIndex);
      }
      continue;
    }

    const fields = operationFields(value);
    if (index >= 0) {
      collection[index] = mergeDefined(collection[index]!, fields);
      consumed.add(index);
    } else {
      collection.push(fields);
      consumed.add(collection.length - 1);
    }
  }
  return collection;
}

function valueAt(root: unknown, path: string[]): unknown {
  let current = root;
  for (const part of path) {
    if (!isRecord(current)) return undefined;
    current = current[part];
  }
  return current;
}

function setAt(root: AnyRecord, path: string[], value: unknown): void {
  let current = root;
  for (let index = 0; index < path.length - 1; index += 1) {
    const part = path[index]!;
    const next = current[part];
    current[part] = isRecord(next) ? { ...next } : {};
    current = current[part] as AnyRecord;
  }
  current[path[path.length - 1]!] = value;
}

function mergeSections(current: AnyRecord, data: AnyRecord): AnyRecord {
  const merged: AnyRecord = { ...current, ...data };
  for (const section of [
    "bodyData",
    "obGynHistory",
    "maleHistory",
    "medicalHistory",
    "lifestyle",
    "familyHistory",
  ]) {
    if (isRecord(data[section])) {
      merged[section] = mergeDefined(
        isRecord(current[section]) ? current[section] : {},
        data[section],
      );
    }
  }

  if (isRecord(data.fertilityHistory)) {
    const currentFertility = isRecord(current.fertilityHistory)
      ? current.fertilityHistory
      : {};
    const fertility = mergeDefined(currentFertility, data.fertilityHistory);
    if (isRecord(data.fertilityHistory.infertilityHistory)) {
      fertility.infertilityHistory = mergeDefined(
        isRecord(currentFertility.infertilityHistory)
          ? currentFertility.infertilityHistory
          : {},
        data.fertilityHistory.infertilityHistory,
      );
    }
    merged.fertilityHistory = fertility;
  }

  if (isRecord(data.fertilityIntent)) {
    merged.fertilityIntent = mergeDefined(
      isRecord(current.fertilityIntent) ? current.fertilityIntent : {},
      data.fertilityIntent,
    );
  }

  for (const section of ["obGynHistory", "maleHistory"]) {
    const incomingSection = data[section];
    const currentSection = current[section];
    const mergedSection = merged[section];
    if (
      isRecord(incomingSection) &&
      isRecord(mergedSection) &&
      isRecord(incomingSection.menstrualHealth)
    ) {
      mergedSection.menstrualHealth = mergeDefined(
        isRecord(currentSection) && isRecord(currentSection.menstrualHealth)
          ? currentSection.menstrualHealth
          : {},
        incomingSection.menstrualHealth,
      );
    }
    if (
      isRecord(incomingSection) &&
      isRecord(mergedSection) &&
      isRecord(incomingSection.sexualHealth)
    ) {
      mergedSection.sexualHealth = mergeDefined(
        isRecord(currentSection) && isRecord(currentSection.sexualHealth)
          ? currentSection.sexualHealth
          : {},
        incomingSection.sexualHealth,
      );
    }
  }
  return merged;
}

function applyDescriptor(
  merged: AnyRecord,
  current: AnyRecord,
  data: AnyRecord,
  descriptor: CollectionDescriptor,
): void {
  if (descriptor.path.includes("*")) {
    // Nested cancer treatment operations are reconciled when their parent
    // cancer row is merged. Their parent identity is stable at this point.
    const parentPath = descriptor.path.slice(0, descriptor.path.indexOf("*"));
    const childPath = descriptor.path.slice(descriptor.path.indexOf("*") + 1);
    const incomingParents = valueAt(data, parentPath);
    const mergedParents = valueAt(merged, parentPath);
    if (!Array.isArray(incomingParents) || !Array.isArray(mergedParents)) {
      return;
    }
    for (const incomingParent of incomingParents) {
      if (!isRecord(incomingParent)) continue;
      const parentIndex = matchingIndex(
        mergedParents.filter(isRecord),
        incomingParent,
        ["cancer", "type", "cancerType", "other"],
        new Set(),
      );
      if (parentIndex < 0) continue;
      const incomingChildren = valueAt(incomingParent, childPath);
      if (!Array.isArray(incomingChildren)) continue;
      const mergedParent = mergedParents[parentIndex];
      if (!isRecord(mergedParent)) continue;
      setAt(
        mergedParent,
        childPath,
        reconcileCollection(
          valueAt(mergedParent, childPath),
          incomingChildren,
          descriptor.identityKeys,
        ),
      );
    }
    return;
  }

  const incoming = valueAt(data, descriptor.path);
  if (!Array.isArray(incoming)) return;
  if (!incoming.some(isRecord)) return;
  setAt(
    merged,
    descriptor.path,
    reconcileCollection(
      valueAt(current, descriptor.path),
      incoming,
      descriptor.identityKeys,
    ),
  );
}

export function mergePatientUpdate(
  current: unknown,
  data: AnyRecord,
): AnyRecord {
  const currentRecord = isRecord(current) ? current : {};
  const merged = mergeSections(currentRecord, data);
  for (const descriptor of COLLECTIONS) {
    applyDescriptor(merged, currentRecord, data, descriptor);
  }
  return merged;
}

function rebaseCollectionOperations(
  operations: unknown,
  serverCollection: unknown,
  identityKeys: string[],
): void {
  if (!Array.isArray(operations) || !Array.isArray(serverCollection)) return;
  const available = serverCollection.filter(isRecord);
  const consumed = new Set<number>();
  for (const value of operations) {
    if (!isRecord(value)) continue;
    if (typeof value.id === "string") {
      const exactIndex = available.findIndex(
        (record) => record.id === value.id,
      );
      if (exactIndex >= 0) consumed.add(exactIndex);
      continue;
    }
    const index = matchingIndex(available, value, identityKeys, consumed);
    if (index < 0) continue;
    consumed.add(index);
    const id = available[index]?.id;
    if (typeof id === "string") value.id = id;
  }
}

function rebaseNestedCollectionOperations(
  reconciled: AnyRecord,
  serverPatient: AnyRecord,
  descriptor: CollectionDescriptor,
): void {
  const wildcardIndex = descriptor.path.indexOf("*");
  const parentPath = descriptor.path.slice(0, wildcardIndex);
  const childPath = descriptor.path.slice(wildcardIndex + 1);
  const incomingParents = valueAt(reconciled, parentPath);
  const serverParents = valueAt(serverPatient, parentPath);
  if (!Array.isArray(incomingParents) || !Array.isArray(serverParents)) return;

  const availableParents = serverParents.filter(isRecord);
  const consumedParents = new Set<number>();
  for (const incomingParent of incomingParents) {
    if (!isRecord(incomingParent) || incomingParent.op === "delete") continue;
    const parentIndex = matchingIndex(
      availableParents,
      incomingParent,
      ["cancer", "type", "cancerType", "other"],
      consumedParents,
    );
    if (parentIndex < 0) continue;
    consumedParents.add(parentIndex);
    rebaseCollectionOperations(
      valueAt(incomingParent, childPath),
      valueAt(availableParents[parentIndex], childPath),
      descriptor.identityKeys,
    );
  }
}

/**
 * Rebase a later queued body on a delivered server patient. In particular,
 * server-assigned IDs are copied into no-ID operations one-to-one, so a second
 * pregnancy or quick-add edit updates the optimistic row created by the first
 * write instead of recreating it.
 */
export function reconcilePatientWriteBody(
  body: AnyRecord,
  serverPatient: unknown,
): AnyRecord {
  if (!isRecord(serverPatient)) return body;
  const reconciled = JSON.parse(JSON.stringify(body)) as AnyRecord;

  for (const descriptor of COLLECTIONS) {
    if (descriptor.path.includes("*")) {
      rebaseNestedCollectionOperations(reconciled, serverPatient, descriptor);
      continue;
    }
    rebaseCollectionOperations(
      valueAt(reconciled, descriptor.path),
      valueAt(serverPatient, descriptor.path),
      descriptor.identityKeys,
    );
  }

  for (const section of [
    "fertilityHistory",
    "fertilityIntent",
    "obGynHistory",
    "maleHistory",
    "medicalHistory",
    "lifestyle",
    "familyHistory",
  ]) {
    const incoming = reconciled[section];
    const server = serverPatient[section];
    if (
      isRecord(incoming) &&
      isRecord(server) &&
      incoming.id === undefined &&
      typeof server.id === "string"
    ) {
      incoming.id = server.id;
    }
  }

  for (const [section, singleton] of [
    ["fertilityHistory", "infertilityHistory"],
    ["obGynHistory", "menstrualHealth"],
    ["obGynHistory", "sexualHealth"],
    ["maleHistory", "sexualHealth"],
  ] as const) {
    const incomingSection = reconciled[section];
    const serverSection = serverPatient[section];
    if (!isRecord(incomingSection) || !isRecord(serverSection)) continue;
    const incoming = incomingSection[singleton];
    const server = serverSection[singleton];
    if (
      isRecord(incoming) &&
      isRecord(server) &&
      incoming.id === undefined &&
      typeof server.id === "string"
    ) {
      incoming.id = server.id;
    }
  }
  return reconciled;
}

function stripUnresolvedAnonymousDeletes(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value
      .filter(
        (entry) =>
          !(
            isRecord(entry) &&
            entry.op === "delete" &&
            typeof entry.id !== "string"
          ),
      )
      .map(stripUnresolvedAnonymousDeletes);
  }
  if (!isRecord(value)) return value;
  return Object.fromEntries(
    Object.entries(value).map(([key, nested]) => [
      key,
      stripUnresolvedAnonymousDeletes(nested),
    ]),
  );
}

/**
 * Anonymous delete markers express removal of optimistic rows whose server ID
 * is still owned by an older FIFO entry. Reconciliation fills those IDs after
 * the older write succeeds. If it never created the row, omit the unresolved
 * marker from the wire body rather than sending an invalid delete.
 */
export function preparePatientWriteBodyForDelivery(body: AnyRecord): AnyRecord {
  return stripUnresolvedAnonymousDeletes(body) as AnyRecord;
}
